import { z } from "zod";

const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const IdentifierSchema = z.string().min(1).max(160);
const PointSchema = z
  .object({
    x: z.number().finite(),
    y: z.number().finite(),
  })
  .strict();

export const VisualActivityPhaseSchema = z.enum([
  "explain",
  "model",
  "guided_practice",
  "diagnose",
  "fade_scaffold",
  "independent_transfer",
  "summary",
  "retention",
]);

export const ActivityVisualBindingSchema = z
  .object({
    materialId: IdentifierSchema,
    role: z.enum(["task_stimulus", "instructional_support"]),
    requirement: z.enum(["required", "optional"]),
    initialVisibility: z.enum(["open", "tray"]),
  })
  .strict();
export type ActivityVisualBinding = z.infer<typeof ActivityVisualBindingSchema>;

const VisualSourceBindingSchema = z
  .object({
    sourceId: IdentifierSchema,
    bookId: IdentifierSchema,
    pdfPage: z.number().int().positive(),
    printedPage: z.number().int().positive(),
    sourcePdfSha256: Sha256Schema,
    imageSha256: Sha256Schema,
    reviewArtifactSha256: Sha256Schema,
    derivation: z.enum(["deterministic_redraw", "source_crop"]),
  })
  .strict();

const SourceCropBindingSchema = VisualSourceBindingSchema.extend({
  privateImagePath: z.string().min(1).max(1_000),
  imageWidth: z.number().int().positive(),
  imageHeight: z.number().int().positive(),
  cropBox: z
    .object({
      x: z.number().int().nonnegative(),
      y: z.number().int().nonnegative(),
      width: z.number().int().positive(),
      height: z.number().int().positive(),
    })
    .strict(),
  accessPolicy: z.literal("private_family_session"),
}).strict();

const VisualAccessibilitySchema = z
  .object({
    label: z.string().min(1).max(200),
    description: z.string().min(1).max(1_200),
  })
  .strict();

const VisualLayerSchema = z
  .object({
    layerId: IdentifierSchema,
    kind: z.enum(["base", "instructional_overlay"]),
    elementIds: z.array(IdentifierSchema).min(1).max(80),
  })
  .strict();

const ElementIdentityShape = {
  elementId: IdentifierSchema,
  layerId: IdentifierSchema,
  label: z.string().min(1).max(240),
};

const CoordinateElementSchema = z.discriminatedUnion("type", [
  z
    .object({
      ...ElementIdentityShape,
      type: z.literal("curve"),
      points: z.array(PointSchema).min(2).max(500),
    })
    .strict(),
  z
    .object({
      ...ElementIdentityShape,
      type: z.literal("point"),
      x: z.number().finite(),
      y: z.number().finite(),
    })
    .strict(),
  z
    .object({
      ...ElementIdentityShape,
      type: z.literal("segment"),
      from: PointSchema,
      to: PointSchema,
    })
    .strict(),
  z
    .object({
      ...ElementIdentityShape,
      type: z.literal("projection"),
      from: PointSchema,
      to: PointSchema,
    })
    .strict(),
  z
    .object({
      ...ElementIdentityShape,
      type: z.literal("ray"),
      origin: PointSchema,
      through: PointSchema,
    })
    .strict(),
  z
    .object({
      ...ElementIdentityShape,
      type: z.literal("circle"),
      center: PointSchema,
      radius: z.number().positive().finite(),
    })
    .strict(),
  z
    .object({
      ...ElementIdentityShape,
      type: z.literal("label"),
      text: z.string().min(1).max(120),
      at: PointSchema,
    })
    .strict(),
]);

const NumberLineElementSchema = z
  .object({
    ...ElementIdentityShape,
    type: z.literal("interval"),
    start: z.number().finite(),
    end: z.number().finite(),
    startClosed: z.boolean(),
    endClosed: z.boolean(),
    extendsLeft: z.boolean(),
    extendsRight: z.boolean(),
    track: z.number().int().min(0).max(8),
  })
  .strict();

const SourceRegionElementSchema = z
  .object({
    ...ElementIdentityShape,
    type: z.literal("region"),
    normalizedBox: z
      .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().positive().max(1),
        height: z.number().positive().max(1),
      })
      .strict(),
  })
  .strict();

const VisualAuditSchema = z
  .object({
    status: z.literal("canonical_reviewed"),
    sourceBindingVerified: z.literal(true),
    mathematicsReview: z.literal("passed"),
    baseAnswerDisclosureReview: z.literal("passed"),
    reviewedBy: z.string().min(1).max(240),
    reviewedAt: z.string().datetime({ offset: true }),
  })
  .strict();

const VisualMaterialBaseShape = {
  materialId: IdentifierSchema,
  lessonId: IdentifierSchema,
  name: z.string().min(1).max(200),
  purpose: z.string().min(1).max(600),
  accessibility: VisualAccessibilitySchema,
  layers: z.array(VisualLayerSchema).min(1).max(24),
  specHash: Sha256Schema,
  audit: VisualAuditSchema,
};

export const VisualMaterialSchema = z.discriminatedUnion("kind", [
  z
    .object({
      ...VisualMaterialBaseShape,
      kind: z.literal("coordinate_plane"),
      sourceBinding: VisualSourceBindingSchema,
      xDomain: z.tuple([z.number().finite(), z.number().finite()]),
      yDomain: z.tuple([z.number().finite(), z.number().finite()]),
      axisLabels: z
        .object({
          x: z.string().min(1).max(24),
          y: z.string().min(1).max(24),
        })
        .strict()
        .optional(),
      xTicks: z.array(z.number().finite()).min(2).max(40),
      yTicks: z.array(z.number().finite()).min(2).max(40),
      elements: z.array(CoordinateElementSchema).min(1).max(120),
    })
    .strict(),
  z
    .object({
      ...VisualMaterialBaseShape,
      kind: z.literal("number_line"),
      sourceBinding: VisualSourceBindingSchema,
      domain: z.tuple([z.number().finite(), z.number().finite()]),
      ticks: z.array(z.number().finite()).min(2).max(80),
      elements: z.array(NumberLineElementSchema).min(1).max(80),
    })
    .strict(),
  z
    .object({
      ...VisualMaterialBaseShape,
      kind: z.literal("source_crop"),
      sourceBinding: SourceCropBindingSchema,
      elements: z.array(SourceRegionElementSchema).min(1).max(80),
    })
    .strict(),
]);
export type VisualMaterial = z.infer<typeof VisualMaterialSchema>;

export const VisualMaterialManifestSchema = z
  .object({
    schema: z.literal("mathos.v2.visual_material_manifest.v1"),
    generatedAt: z.string().datetime({ offset: true }),
    materialCount: z.number().int().nonnegative(),
    materials: z.array(VisualMaterialSchema),
  })
  .strict();
export type VisualMaterialManifest = z.infer<typeof VisualMaterialManifestSchema>;

export const ActivityVisualClassificationSchema = z
  .object({
    lessonId: IdentifierSchema,
    lessonTitle: z.string().min(1).max(240),
    familyId: IdentifierSchema,
    activityId: IdentifierSchema,
    phase: VisualActivityPhaseSchema,
    independence: z.enum(["teacher_led", "guided", "independent"]),
    status: z.enum(["none", "ready", "required_but_missing", "optional"]),
    bindings: z.array(ActivityVisualBindingSchema).max(12),
    rationale: z.string().min(1).max(1_200),
  })
  .strict();
export type ActivityVisualClassification = z.infer<
  typeof ActivityVisualClassificationSchema
>;

const VisualStatusCountsSchema = z
  .object({
    none: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
    required_but_missing: z.number().int().nonnegative(),
    optional: z.number().int().nonnegative(),
  })
  .strict();

export const ActivityVisualClassificationManifestSchema = z
  .object({
    schema: z.literal("mathos.v2.activity_visual_classification.v1"),
    generatedAt: z.string().datetime({ offset: true }),
    reviewMethod: z.string().min(1).max(1_000),
    summary: z
      .object({
        lessonCount: z.number().int().nonnegative(),
        activityCount: z.number().int().nonnegative(),
        statusCounts: VisualStatusCountsSchema,
        lessonStatusCounts: VisualStatusCountsSchema,
      })
      .strict(),
    activities: z.array(ActivityVisualClassificationSchema),
  })
  .strict();
export type ActivityVisualClassificationManifest = z.infer<
  typeof ActivityVisualClassificationManifestSchema
>;

const VisualExampleGridSchema = z
  .object({
    title: z.string().min(1).max(120),
    cells: z
      .array(z.array(z.string().min(1).max(80)).min(1).max(6))
      .min(1)
      .max(6),
    highlightedCells: z
      .array(
        z
          .object({
            row: z.number().int().min(0).max(5),
            column: z.number().int().min(0).max(5),
          })
          .strict(),
      )
      .max(36)
      .optional(),
    caption: z.string().min(1).max(320).optional(),
  })
  .strict()
  .superRefine((example, context) => {
    const columnCount = example.cells[0]?.length ?? 0;
    if (example.cells.some((row) => row.length !== columnCount)) {
      context.addIssue({
        code: "custom",
        message: "visual example grid rows must have equal length",
      });
    }
    for (const [index, cell] of (example.highlightedCells ?? []).entries()) {
      if (cell.row >= example.cells.length || cell.column >= columnCount) {
        context.addIssue({
          code: "custom",
          path: ["highlightedCells", index],
          message: "highlighted visual grid cell must exist",
        });
      }
    }
  });

export const VisualOperationSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("focus_visual_elements"),
      materialId: IdentifierSchema,
      elementIds: z.array(IdentifierSchema).min(1).max(24),
    })
    .strict(),
  z
    .object({
      type: z.literal("clear_visual_focus"),
      materialId: IdentifierSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("activate_optional_material"),
      materialId: IdentifierSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("show_visual_example_grid"),
      materialId: IdentifierSchema,
      example: VisualExampleGridSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("clear_visual_example_grid"),
      materialId: IdentifierSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("show_teacher_example_grid"),
      example: VisualExampleGridSchema,
    })
    .strict(),
  z.object({ type: z.literal("clear_teacher_example_grid") }).strict(),
]);
export type VisualOperation = z.infer<typeof VisualOperationSchema>;

export type VisualExampleGrid = z.infer<typeof VisualExampleGridSchema>;

export const VisualBoardStateSchema = z
  .object({
    materials: z.array(
      z
        .object({
          materialId: IdentifierSchema,
          visible: z.boolean(),
          focusedElementIds: z.array(IdentifierSchema).max(24),
          exampleGrid: z
            .object({
              title: z.string().min(1).max(120),
              cells: z
                .array(z.array(z.string().min(1).max(80)).min(1).max(6))
                .min(1)
                .max(6),
              highlightedCells: z
                .array(
                  z
                    .object({
                      row: z.number().int().min(0).max(5),
                      column: z.number().int().min(0).max(5),
                    })
                    .strict(),
                )
                .max(36)
                .optional(),
              caption: z.string().min(1).max(320).optional(),
            })
            .strict()
            .optional(),
        })
        .strict(),
    ),
    teacherExampleGrid: VisualExampleGridSchema.optional(),
  })
  .strict();
export type VisualBoardState = z.infer<typeof VisualBoardStateSchema>;

export interface VisualCatalogIssue {
  path: string;
  message: string;
}

export function inspectVisualCatalog(
  classification: ActivityVisualClassificationManifest,
  manifest: VisualMaterialManifest,
): VisualCatalogIssue[] {
  const issues: VisualCatalogIssue[] = [];
  const materials = new Map<string, VisualMaterial>();
  for (const material of manifest.materials) {
    if (materials.has(material.materialId)) {
      issues.push({
        path: `materials.${material.materialId}`,
        message: "materialId is duplicated",
      });
    }
    materials.set(material.materialId, material);
    inspectMaterial(material, issues);
  }
  if (manifest.materialCount !== manifest.materials.length) {
    issues.push({ path: "materialCount", message: "materialCount does not match materials" });
  }
  if (classification.summary.activityCount !== classification.activities.length) {
    issues.push({ path: "summary.activityCount", message: "activityCount does not match activities" });
  }

  const activityKeys = new Set<string>();
  const instructionalByLesson = new Map<string, Set<string>>();
  const assessmentByLesson = new Map<string, Set<string>>();
  for (const activity of classification.activities) {
    const activityPath = `${activity.lessonId}/${activity.activityId}`;
    if (activityKeys.has(activityPath)) {
      issues.push({ path: activityPath, message: "activity classification is duplicated" });
    }
    activityKeys.add(activityPath);
    const independent =
      activity.independence === "independent" || activity.phase === "retention";
    const missingRequired = activity.bindings.some(
      (binding) =>
        binding.requirement === "required" && !materials.has(binding.materialId),
    );
    if (activity.status === "ready" && activity.bindings.some((binding) => !materials.has(binding.materialId))) {
      issues.push({ path: activityPath, message: "ready activity references a missing material" });
    }
    if (activity.status === "required_but_missing" && !missingRequired) {
      issues.push({ path: activityPath, message: "required_but_missing has no missing required material" });
    }
    for (const binding of activity.bindings) {
      const material = materials.get(binding.materialId);
      if (
        (binding.requirement === "required" && binding.initialVisibility !== "open") ||
        (binding.requirement === "optional" && binding.initialVisibility !== "tray")
      ) {
        issues.push({
          path: activityPath,
          message: "required materials open automatically and optional materials start in the tray",
        });
      }
      if (material && material.lessonId !== activity.lessonId) {
        issues.push({ path: activityPath, message: `${binding.materialId} belongs to another lesson` });
      }
      if (independent && binding.role !== "task_stimulus") {
        issues.push({ path: activityPath, message: "independent and retention activities only accept task stimuli" });
      }
      if (independent) {
        const ids = assessmentByLesson.get(activity.lessonId) ?? new Set<string>();
        ids.add(binding.materialId);
        assessmentByLesson.set(activity.lessonId, ids);
        if (material?.layers.some((layer) => layer.kind === "instructional_overlay")) {
          issues.push({ path: activityPath, message: "assessment stimulus contains an instructional overlay" });
        }
      } else if (binding.role === "instructional_support") {
        const ids = instructionalByLesson.get(activity.lessonId) ?? new Set<string>();
        ids.add(binding.materialId);
        instructionalByLesson.set(activity.lessonId, ids);
      }
    }
  }

  for (const [lessonId, instructional] of instructionalByLesson) {
    const assessment = assessmentByLesson.get(lessonId) ?? new Set<string>();
    for (const materialId of instructional) {
      if (assessment.has(materialId)) {
        issues.push({
          path: lessonId,
          message: `${materialId} is reused as instructional support and assessment stimulus`,
        });
      }
    }
  }
  return issues;
}

function inspectMaterial(
  material: VisualMaterial,
  issues: VisualCatalogIssue[],
): void {
  const path = `materials.${material.materialId}`;
  const elements = new Map(material.elements.map((element) => [element.elementId, element]));
  const assigned = new Set<string>();
  for (const layer of material.layers) {
    for (const elementId of layer.elementIds) {
      const element = elements.get(elementId);
      if (!element) {
        issues.push({ path, message: `layer ${layer.layerId} references unknown ${elementId}` });
      } else if (element.layerId !== layer.layerId) {
        issues.push({ path, message: `${elementId} has a mismatched layerId` });
      }
      if (assigned.has(elementId)) {
        issues.push({ path, message: `${elementId} appears in more than one layer` });
      }
      assigned.add(elementId);
    }
  }
  for (const elementId of elements.keys()) {
    if (!assigned.has(elementId)) {
      issues.push({ path, message: `${elementId} is not assigned to a layer` });
    }
  }
  if (!material.layers.some((layer) => layer.kind === "base")) {
    issues.push({ path, message: "a base layer is required" });
  }
  if (material.kind === "coordinate_plane") {
    inspectDomain(path, "xDomain", material.xDomain, issues);
    inspectDomain(path, "yDomain", material.yDomain, issues);
  } else if (material.kind === "number_line") {
    inspectDomain(path, "domain", material.domain, issues);
  } else {
    const { cropBox, imageWidth, imageHeight } = material.sourceBinding;
    if (
      cropBox.x + cropBox.width > imageWidth ||
      cropBox.y + cropBox.height > imageHeight
    ) {
      issues.push({ path, message: "cropBox exceeds the private source image" });
    }
  }
}

function inspectDomain(
  path: string,
  field: string,
  domain: readonly [number, number],
  issues: VisualCatalogIssue[],
): void {
  if (domain[0] >= domain[1]) {
    issues.push({ path: `${path}.${field}`, message: "domain endpoints are not increasing" });
  }
}
